import {
  FlyoutListMenuWidget, HorizontalOriginOption,
  MenuButtonWidget,
  MenuItemInfo, OriginOptions, VerticalOriginOption
} from '@tableau/widgets';
import React from 'react';

import {ToasterService, ToastType} from '~commonInfra/services/ToasterService';
import {Messages} from '~src/codegen/Localize';
import {ResultHelper} from '~src/httpClient/utils/ResultHelper';
import {KeyDeletionConfirmationDialog} from '~src/kmsInOnline/components/dialogs/KeyDeletionDialog';
import {keyRotationConfirmationDialog} from '~src/kmsInOnline/components/dialogs/KeyRotationConfirmationDialog';
import {IGetSiteEncryptionKeyResult} from '~src/kmsInOnline/serverApi/GetSiteEncryptionKeyRequest';
import {DeleteSiteEncryptionKeyService} from '~src/kmsInOnline/services/DeleteSiteEncryptionKeyService';
import {
  RotateSiteEncryptionKeyService
} from '~src/kmsInOnline/services/RotateSiteEncryptionKeyService';
import {
  retrieveExtractKey
} from '~src/settings/general-settings/components/sections/kms-in-online/KMSInOnlineSection';
import hasErrors = ResultHelper.hasErrors;

export const KEY_OPERATIONS_MENU_TEST_ID = 'extractEncryptionInTOLKeyOperationsMenu';

export const ROTATE_KEY = 'rotate_key';
export const DELETE_KEY = 'delete_key';

export function keyOperationsMenuItems(): MenuItemInfo[] {
  return [
    { displayValue: Messages.extractEncryptionTOLKeyRotationDropdownOption(), value: ROTATE_KEY },
    { displayValue: Messages.extractEncryptionTOLKeyDeletionDropdownOption(), value: DELETE_KEY }
  ];
}
const TopRightOrigin: OriginOptions = {
  horizontal: HorizontalOriginOption.left,
  vertical: VerticalOriginOption.bottom,
};

export const KeyOperationsMenu = function(siteEncryptionKey: IGetSiteEncryptionKeyResult, setSiteEncryptionKey: (s: IGetSiteEncryptionKeyResult) => void) {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [buttonEl, setButtonEl] = React.useState<HTMLElement | null>(null);

  const handleClick = () => setIsOpen(!isOpen);
  const handleMenuClose = React.useCallback(() => setIsOpen(false), []);
  const handleMenuSelect = React.useCallback((value: string) => {
    if (value === ROTATE_KEY) {
      keyRotationConfirmationDialog().then(() => {
        RotateSiteEncryptionKeyService.rotateSiteEncryptionKey()
          .then(() => keyRotationOnSuccess(siteEncryptionKey, setSiteEncryptionKey), keyRotationOnFailure);
      }).catch(() => {});
    } else {
      KeyDeletionConfirmationDialog().then(() => {
        DeleteSiteEncryptionKeyService.deleteSiteEncryptionKey()
          .then(() => keyDeletionOnSuccess(siteEncryptionKey, setSiteEncryptionKey), keyDeletionOnFailure);
      }).catch(() => {});
    }
    setIsOpen(false);
  }, [setSiteEncryptionKey, siteEncryptionKey]);

  return (!hasErrors(siteEncryptionKey)) ? <>
    <MenuButtonWidget
      aria-controls={'keyOperationsMenu'}
      children={Messages.extractEncryptionTOLKeyOperationsDropdown()}
      isOpen={isOpen}
      onClick={handleClick}
      ref={setButtonEl}
      testId={KEY_OPERATIONS_MENU_TEST_ID}
    />
    {isOpen && <FlyoutListMenuWidget
      allowAutoPosition
      anchorEl={buttonEl}
      anchorOrigin={TopRightOrigin}
      id={'keyOperationsMenu'}
      maxHeight={250} // this could also be passed in the `style` prop
      menuItemInfos={keyOperationsMenuItems()}
      menuStyle={{ width: 200 }} // the menu will elide any items too long to fit when width-constrained
      onSelect={handleMenuSelect}
      requestCloseAll={handleMenuClose}
      testId='keyOperationsFlyoutMenu'
      toggleEl={buttonEl}
    />}
  </>: null;
};

/**
 * Alert user that key rotation succeeded
 */
async function keyRotationOnSuccess(siteEncryptionKey: IGetSiteEncryptionKeyResult, setSiteEncryptionKey: (s: IGetSiteEncryptionKeyResult) => void) {
  let response = await retrieveExtractKey();
  setSiteEncryptionKey({...response});
  ToasterService.instance.pop({ type: ToastType.Success, text: Messages.extractEncryptionTOLKeyRotationSucceeded() });
}

/**
 * Alert user that key rotation failed
 */
function keyRotationOnFailure() {
  ToasterService.instance.pop({ type: ToastType.Error, text: Messages.extractEncryptionTOLKeyRotationFailed() });
}

/**
 * Alert user that key deletion succeeded
 */
async function keyDeletionOnSuccess(siteEncryptionKey: IGetSiteEncryptionKeyResult, setSiteEncryptionKey: (s: IGetSiteEncryptionKeyResult) => void) {
  let response = await retrieveExtractKey();
  setSiteEncryptionKey({...response});
  ToasterService.instance.pop({ type: ToastType.Success, text: Messages.extractEncryptionTOLKeyDeletionSucceeded() });
}

/**
 * Alert user that key deletion failed
 */
function keyDeletionOnFailure() {
  ToasterService.instance.pop({ type: ToastType.Error, text: Messages.extractEncryptionTOLKeyDeletionFailed() });
}
